import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Check, ChevronDown } from 'lucide-react';
import {
  getStoredConsent,
  acceptAll,
  rejectAll,
  saveCustom,
  revokeConsent,
  initAnalytics,
  type ConsentSettings,
} from '../utils/analytics';

type BannerView = 'main' | 'settings';

const DEFAULT_CUSTOM: ConsentSettings = {
  analytics_storage: 'granted',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'granted',
};

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<BannerView>('main');
  const [customSettings, setCustomSettings] = useState<ConsentSettings>(DEFAULT_CUSTOM);

  useEffect(() => {
    initAnalytics();
    const stored = getStoredConsent();
    if (!stored) {
      // No choice yet — show banner after a short delay
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Allow reopening from footer link
  useEffect(() => {
    const handler = () => {
      setView('settings');
      const stored = getStoredConsent();
      setCustomSettings(stored?.settings ?? DEFAULT_CUSTOM);
      setVisible(true);
    };
    window.addEventListener('open-cookie-settings', handler);
    return () => window.removeEventListener('open-cookie-settings', handler);
  }, []);

  const handleAccept = () => {
    acceptAll();
    setVisible(false);
  };

  const handleReject = () => {
    rejectAll();
    setVisible(false);
  };

  const handleSaveCustom = () => {
    saveCustom(customSettings);
    setVisible(false);
  };

  const handleWithdraw = () => {
    revokeConsent();
    setVisible(false);
  };

  const toggleSetting = (key: keyof ConsentSettings) => {
    setCustomSettings((prev) => ({
      ...prev,
      [key]: prev[key] === 'granted' ? 'denied' : 'granted',
    }));
  };

  const settingRows: { key: keyof ConsentSettings; label: string; desc: string; alwaysOn?: boolean }[] = [
    { key: 'analytics_storage', label: 'Statistik (Google Analytics)', desc: 'Erfasst anonyme Nutzungsdaten zur Website-Verbesserung.' },
    { key: 'ad_storage', label: 'Werbung', desc: 'Speichert Werbe-Cookies für personalisierte Anzeigen.' },
    { key: 'ad_user_data', label: 'Werbe-Nutzerdaten', desc: 'Weitergabe von Nutzerdaten an Werbepartner.' },
    { key: 'ad_personalization', label: 'Personalisierte Werbung', desc: 'Erstellung personalisierter Werbeprofile.' },
    { key: 'functionality_storage', label: 'Funktionale Cookies', desc: 'Speichert Einstellungen wie Sprache für ein besseres Nutzererlebnis.' },
  ];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-[100] flex justify-center px-4 pb-4 pointer-events-none"
        >
          <div className="pointer-events-auto w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <Cookie className="w-5 h-5 text-blue-600" />
                <span className="font-bold text-gray-800">Cookie-Einstellungen</span>
              </div>
              <button
                onClick={() => setVisible(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-200 transition-colors"
                aria-label="Schließen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {view === 'main' ? (
              <div className="px-6 py-5">
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  Wir verwenden Cookies, um die Website zu verbessern und Nutzungsstatistiken zu erfassen
                  (Google Analytics 4). Mit Klick auf „Alle akzeptieren" stimmen Sie der Verarbeitung zu.
                  Sie können auch „Ablehnen" wählen oder einzelne Kategorien unter „Einstellungen" anpassen.
                  Weitere Informationen finden Sie in unserer{' '}
                  <a href="/datenschutz" className="text-blue-600 hover:underline font-medium">Datenschutzerklärung</a>.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAccept}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-sm"
                  >
                    <Check className="w-4 h-4" />
                    Alle akzeptieren
                  </button>
                  <button
                    onClick={handleReject}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors text-sm"
                  >
                    Ablehnen
                  </button>
                  <button
                    onClick={() => setView('settings')}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-colors text-sm"
                  >
                    <ChevronDown className="w-4 h-4" />
                    Einstellungen
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-6 py-5">
                <div className="space-y-2 mb-5">
                  {settingRows.map((row) => (
                    <div
                      key={row.key}
                      className="flex items-start justify-between gap-4 py-2.5 border-b border-gray-100 last:border-0"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{row.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{row.desc}</p>
                      </div>
                      <button
                        onClick={() => !row.alwaysOn && toggleSetting(row.key)}
                        disabled={row.alwaysOn}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                          customSettings[row.key] === 'granted' ? 'bg-blue-600' : 'bg-gray-300'
                        } ${row.alwaysOn ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                        aria-label={`${row.label} ${customSettings[row.key] === 'granted' ? 'deaktivieren' : 'aktivieren'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            customSettings[row.key] === 'granted' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleSaveCustom}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-sm"
                  >
                    Auswahl speichern
                  </button>
                  <button
                    onClick={handleAccept}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors text-sm"
                  >
                    Alle akzeptieren
                  </button>
                  <button
                    onClick={handleWithdraw}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-colors text-sm"
                  >
                    Zurücksetzen
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
